import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import * as Yup from "yup";

const validationSchema = Yup.object({
  name: Yup.string().trim().nullable(),
  documentReference: Yup.string().trim().nullable(),
  includeInactive: Yup.boolean().nullable(),
  createdFrom: Yup.date().nullable(),
  createdTo: Yup.date()
    .nullable()
    .when("createdFrom", (createdFrom, schema) =>
      createdFrom
        ? schema.min(createdFrom, "To date cannot be before From date")
        : // eslint-disable-next-line prettier/prettier
          schema,
    ),
});

const PackageFilters = ({
  onHideFilters,
  onApplyFilters,
  initialValues,
}: any) => {
  const defaultValues = {
    name: "",
    documentReference: "",
    createdFrom: null as Date | null,
    createdTo: null as Date | null,
    includeInactive: false,
  };

  const [values, setValues] = useState({
    name: initialValues?.name || defaultValues.name,
    documentReference:
      initialValues?.documentReference || defaultValues.documentReference,
    createdFrom: initialValues?.createdFrom || defaultValues.createdFrom,
    createdTo: initialValues?.createdTo || defaultValues.createdTo,
    includeInactive:
      initialValues?.includeInactive ?? defaultValues.includeInactive,
  });

  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    setValues({
      name: initialValues?.name || defaultValues.name,
      documentReference:
        initialValues?.documentReference || defaultValues.documentReference,
      createdFrom: initialValues?.createdFrom || defaultValues.createdFrom,
      createdTo: initialValues?.createdTo || defaultValues.createdTo,
      includeInactive:
        initialValues?.includeInactive ?? defaultValues.includeInactive,
    });
  }, [initialValues]);

  const handleChange = (field: string, value: any) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validate = async () => {
    try {
      await validationSchema.validate(values, {
        abortEarly: false,
      });
      setErrors({});
      return true;
    } catch (err: any) {
      const formattedErrors: any = {};
      err.inner.forEach((e: any) => {
        formattedErrors[e.path] = e.message;
      });
      setErrors(formattedErrors);
      return false;
    }
  };

  const handleApply = async () => {
    const isValid = await validate();
    if (!isValid) return;

    onApplyFilters(values);
    onHideFilters();
  };

  const handleClear = () => {
    setValues(defaultValues);
    setErrors({});
    onApplyFilters(defaultValues);
    onHideFilters();
  };

  return (
    <div className="p-3">
      <h3 className="text-xl my-2 font-semibold">Filters</h3>

      <div className="grid grid-cols-1 gap-4">
        <div>
          <InputText
            value={values.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="Search by Name"
            className="w-full"
          />
          {errors.name && <small className="p-error">{errors.name}</small>}
        </div>

        <div>
          <InputText
            value={values.documentReference}
            onChange={(e) => handleChange("documentReference", e.target.value)}
            placeholder="Search by Document Reference"
            className="w-full"
          />
          {errors.documentReference && (
            <small className="p-error">{errors.documentReference}</small>
          )}
        </div>

        <div className="w-full">
          <label className="text-sm mb-1 block">Created Date Range</label>

          <div className="flex gap-4 items-center">
            <div className="w-1/2">
              <Calendar
                value={values.createdFrom}
                onChange={(e) => handleChange("createdFrom", e.value)}
                showIcon
                className="w-full"
                dateFormat="mm/dd/yy"
                placeholder="From"
              />
            </div>

            <div className="w-1/2">
              <Calendar
                value={values.createdTo}
                onChange={(e) => handleChange("createdTo", e.value)}
                showIcon
                className="w-full"
                dateFormat="mm/dd/yy"
                placeholder="To"
              />
            </div>
          </div>

          {errors.createdTo && (
            <small className="p-error">{errors.createdTo}</small>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            inputId="includeInactive"
            checked={values.includeInactive}
            onChange={(e) => handleChange("includeInactive", e.checked)}
          />
          <label htmlFor="includeInactive" className="text-sm">
            Include Inactive
          </label>
        </div>

        <div className="flex flex-row-reverse gap-4">
          <Button
            onClick={handleApply}
            label="Apply"
            className="p-button-sm p-button-outlined p-button-success"
          />
          <Button
            onClick={handleClear}
            label="Clear Filters"
            className="p-button-sm p-button-outlined p-button-warning"
          />
        </div>
      </div>
    </div>
  );
};

export default PackageFilters;
